import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpenText } from "lucide-react";
import SovereignAnchor from "../components/SovereignAnchor";
import useSeo from "../hooks/useSeo";
import { INTEL_POSTS } from "../data/intelPosts";

export default function IntelHub() {
    useSeo({
        title: "Fleet Intel | EIGOO",
        description:
            "Field manuals, signal reports, and engagement strategies from the EIGOO Sovereign Fleet — for operators who treat their storefront like a venue.",
    });

    return (
        <div className="min-h-screen bg-background text-foreground grain-overlay relative" data-testid="intel-hub">
            <div className="absolute inset-0 pointer-events-none opacity-40">
                <div className="absolute top-[-20%] right-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(40_60%_45%/0.10)] blur-[120px]" />
                <div className="absolute bottom-[-20%] left-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(153_50%_25%/0.22)] blur-[120px]" />
            </div>

            <header className="relative border-b border-[hsl(40_40%_25%/0.2)] bg-[hsl(151_55%_7%/0.7)] backdrop-blur-md">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-4">
                    <Link to="/" className="flex items-center gap-3" data-testid="intel-home-link">
                        <SovereignAnchor size={26} strokeWidth={6} className="text-[hsl(45_85%_68%)]" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground">EIGOO · Sovereign Fleet</div>
                            <div className="font-heading text-2xl gold-gradient-text tracking-tight">Fleet Intel</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-4 text-sm">
                        <Link to="/" className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors" data-testid="intel-back-home">
                            ← Main Hub
                        </Link>
                        <Link to="/login" className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors" data-testid="intel-login">
                            Bridge Login
                        </Link>
                    </div>
                </div>
            </header>

            <main className="relative max-w-6xl mx-auto px-6 py-16">
                <div className="max-w-3xl">
                    <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)] mb-4 flex items-center gap-2">
                        <BookOpenText className="w-3 h-3" /> Fleet Field Manual
                    </div>
                    <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter leading-[1.05]">
                        Signal reports for the
                        <span className="gold-gradient-text"> craft of commerce.</span>
                    </h1>
                    <p
                        className="mt-6 text-lg text-foreground/80 leading-relaxed"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic" }}
                    >
                        Engagement playbooks, operator field notes, and quiet strategies for the
                        marketers who run the fleet — published from the bridge, not the boardroom.
                    </p>
                </div>

                <div className="my-12 h-px w-full bg-gradient-to-r from-transparent via-[hsl(40_61%_53%/0.7)] to-transparent" />

                <ul className="grid md:grid-cols-2 gap-6" data-testid="intel-post-list">
                    {[...INTEL_POSTS]
                        .sort((a, b) => (b.publishedAt || "").localeCompare(a.publishedAt || ""))
                        .map((post) => (
                        <li key={post.slug}>
                            <Link
                                to={`/intel/${post.slug}`}
                                data-testid={`intel-post-card-${post.slug}`}
                                className="group block glass rounded-2xl p-7 border border-[hsl(40_40%_25%/0.25)] hover:border-[hsl(45_85%_68%/0.55)] transition-colors"
                            >
                                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
                                    <span>{post.category}</span>
                                    <span>{post.readingMinutes} min read</span>
                                </div>
                                <h2 className="font-heading text-3xl tracking-tight leading-tight">
                                    {post.title}
                                </h2>
                                <p
                                    className="mt-4 text-base text-foreground/75 leading-relaxed"
                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic" }}
                                >
                                    {post.excerpt}
                                </p>
                                <div className="mt-6 flex items-center justify-between">
                                    <span className="text-xs text-muted-foreground">
                                        {new Date(post.publishedAt).toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                        })}
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-[hsl(45_85%_68%)] text-sm font-medium opacity-80 group-hover:opacity-100 transition-opacity">
                                        Read brief
                                        <ArrowUpRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            </main>
        </div>
    );
}
